import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import getHelpIcon from '../../images/GetHelpIcon.svg';
import oneAppIcon from '../../images/OneAppIcon.svg';
import learnIcon from '../../images/LearnSectionIcon.svg';
import emergencyIcon from '../../images/EmergencyIcon.svg';
import phonePic from '../../images/KitoHowItWorks.svg';
import phonePicSw from '../../images/HowItWorksSw.svg';
import childPhone from '../../images/KitoHowItWorksToggle.png';
import childPhoneSw from '../../images/HowItWorksOneAppSw.svg';
import styles from './kitoApp.module.css';

// Each arrow is drawn at runtime between two anchors.
// An anchor is { anchor: <ref key>, ax, ay } => point = rect.left + ax*width, rect.top + ay*height.
// curvature: signed fraction of the origin->destination distance (sign = bulge side, magnitude = intensity).
const ARROWS = [
  { key: 'getHelp', from: { anchor: 'phone', ax: 0.12, ay: 0.30 }, to: { anchor: 'getHelp', ax: 0.90, ay: 0.617 }, curvature: 0.40 },
  { key: 'oneApp', from: { anchor: 'phone', ax: 0.665, ay: 0.07 }, to: { anchor: 'oneApp', ax: -0.029, ay: 0.226 }, curvature: -0.40 },
  { key: 'learn', from: { anchor: 'phone', ax: 0.135, ay: 0.50 }, to: { anchor: 'learn', ax: 0.90, ay: 0.156 }, curvature: 0.40 },
  { key: 'emergency', from: { anchor: 'phone', ax: 0.889, ay: 0.97 }, to: { anchor: 'emergency', ax: -0.029, ay: 0.606 }, curvature: 0.40 },
];

function KitoApp() {
  const { t, i18n } = useTranslation();
  const isSwahili = i18n.language === 'Swahili';
  const stageRef = useRef(null);
  const anchors = useRef({});
  const setAnchor = (key) => (el) => { anchors.current[key] = el; };

  const [paths, setPaths] = useState([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1440px)').matches
  );

  const compute = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const sr = stage.getBoundingClientRect();
    const point = (a) => {
      const el = anchors.current[a.anchor];
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      return { x: r.left + a.ax * r.width - sr.left, y: r.top + a.ay * r.height - sr.top };
    };
    const MAX_HALF = 7; // max half-width of the shaft (grows from 0 to this)
    const HEAD_HALF = 25; // half-width of the arrowhead barbs
    const HEAD_LEN = 45; // length of the arrowhead (tip to barb tips)
    const NECK_FRAC = 0.22; // where the shaft joins, forward from the barbs (0..1 of head length)
    const N = 40; // samples along the curve
    const f = (n) => n.toFixed(1);
    const list = ARROWS.map((cfg) => {
      const o = point(cfg.from);
      const d = point(cfg.to);
      if (!o || !d) return null;
      const dx = d.x - o.x;
      const dy = d.y - o.y;
      const len = Math.hypot(dx, dy) || 1;
      const mx = (o.x + d.x) / 2;
      const my = (o.y + d.y) / 2;
      const n0x = -dy / len; // perpendicular unit
      const n0y = dx / len;
      const h = cfg.curvature * len; // signed sagitta (bulge height)
      const s = h < 0 ? -1 : 1;
      const ah = Math.abs(h) || 1;
      const R = ah / 2 + (len * len) / (8 * ah); // circle radius through o, d with sagitta h
      const ccx = mx + n0x * (h - s * R); // circle center
      const ccy = my + n0y * (h - s * R);
      const apx = mx + n0x * h; // apex (bulge tip) -> defines sweep direction
      const apy = my + n0y * h;
      const a0 = Math.atan2(o.y - ccy, o.x - ccx);
      const a1 = Math.atan2(d.y - ccy, d.x - ccx);
      const dir = Math.sign((o.x - ccx) * (apy - ccy) - (o.y - ccy) * (apx - ccx)) || 1;
      let sweep = a1 - a0;
      if (dir > 0 && sweep < 0) sweep += 2 * Math.PI;
      if (dir < 0 && sweep > 0) sweep -= 2 * Math.PI;
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const ang = a0 + sweep * (i / N);
        const x = ccx + R * Math.cos(ang);
        const y = ccy + R * Math.sin(ang);
        const tgx = -Math.sin(ang) * sweep;
        const tgy = Math.cos(ang) * sweep;
        const tl = Math.hypot(tgx, tgy) || 1;
        pts.push({ x, y, nx: -tgy / tl, ny: tgx / tl });
      }
      const cum = [0];
      for (let i = 1; i <= N; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
      const total = cum[N] || 1;
      const headLen = Math.min(HEAD_LEN, total * 0.5);
      const barbLen = total - headLen; // arc-length of the barb tips (back of head)
      const neckLen = total - headLen * (1 - NECK_FRAC); // where the shaft joins (forward of barbs)
      const at = (L) => {
        let i = 0;
        while (i < N && cum[i + 1] < L) i++;
        const s = (L - cum[i]) / ((cum[i + 1] - cum[i]) || 1);
        return {
          x: pts[i].x + (pts[i + 1].x - pts[i].x) * s,
          y: pts[i].y + (pts[i + 1].y - pts[i].y) * s,
          nx: pts[i].nx,
          ny: pts[i].ny,
        };
      };
      const left = [];
      const right = [];
      for (let i = 0; i <= N; i++) {
        if (cum[i] > neckLen) break;
        const hw = neckLen > 0 ? MAX_HALF * (cum[i] / neckLen) : 0;
        left.push([pts[i].x + pts[i].nx * hw, pts[i].y + pts[i].ny * hw]);
        right.push([pts[i].x - pts[i].nx * hw, pts[i].y - pts[i].ny * hw]);
      }
      const nk = at(neckLen);
      left.push([nk.x + nk.nx * MAX_HALF, nk.y + nk.ny * MAX_HALF]);
      right.push([nk.x - nk.nx * MAX_HALF, nk.y - nk.ny * MAX_HALF]);
      const bb = at(barbLen);
      let dPath = 'M ' + left.map((p) => f(p[0]) + ' ' + f(p[1])).join(' L ');
      dPath += ' L ' + f(bb.x + bb.nx * HEAD_HALF) + ' ' + f(bb.y + bb.ny * HEAD_HALF);
      dPath += ' L ' + f(d.x) + ' ' + f(d.y);
      dPath += ' L ' + f(bb.x - bb.nx * HEAD_HALF) + ' ' + f(bb.y - bb.ny * HEAD_HALF);
      for (let i = right.length - 1; i >= 0; i--) dPath += ' L ' + f(right[i][0]) + ' ' + f(right[i][1]);
      dPath += ' Z';
      return { key: cfg.key, d: dPath };
    }).filter(Boolean);
    setSize({ w: sr.width, h: sr.height });
    setPaths(list);
  }, []);

  useLayoutEffect(() => {
    compute();
    const ro = new ResizeObserver(() => compute());
    if (stageRef.current) ro.observe(stageRef.current);
    Object.values(anchors.current).forEach((el) => el && ro.observe(el));
    window.addEventListener('resize', compute);
    window.addEventListener('load', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('load', compute);
    };
  }, [compute, isDesktop]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1440px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <section className={styles.container} id='howItWorks'>
      <div className={styles.headers}>
        <span className={styles.badgeFull}>{t('kitoApp.badge')}</span>
        <span className={styles.badgeShort}>{t('kitoApp.badgeShort')}</span>
        <h2>{t('kitoApp.h2')}</h2>
        <p>{t('kitoApp.p')}</p>
      </div>

      <div className={styles.stage} ref={stageRef}>
        <div className={styles.circle} />
        <div className={styles.phone} ref={setAnchor('phone')}>
          <img src={isSwahili ? phonePicSw : phonePic} alt="Kito Health app" />
        </div>
        <img
          className={styles.childPhone}
          src={isSwahili ? childPhoneSw : childPhone}
          alt="Kito Health Kids app"
        />

        <article className={`${styles.card} ${styles.cardGetHelp}`} ref={setAnchor('getHelp')}>
          <div className={styles.cardHead}>
            <img src={getHelpIcon} alt="" />
            <h3>{t('kitoApp.getHelp.title')}</h3>
          </div>
          <ol className={styles.cardList}>
            <li>
              <p className='bold'>{t('kitoApp.getHelp.item1Title')}</p>
              <p>{t('kitoApp.getHelp.item1Text')}</p>
            </li>
            <li>
              <p className='bold'>{t('kitoApp.getHelp.item2Title')}</p>
              <p>{t('kitoApp.getHelp.item2Text')}</p>
            </li>
          </ol>
        </article>

        <article className={`${styles.card} ${styles.cardOneApp}`} ref={setAnchor('oneApp')}>
          <div className={styles.cardHead}>
            <img src={oneAppIcon} alt="" />
            <h3>{t('kitoApp.oneApp.title')}</h3>
          </div>
          <div className={styles.cardText}>
            <p className='bold'>{t('kitoApp.oneApp.subtitle')}</p>
            <p>{t('kitoApp.oneApp.text')}</p>
          </div>
        </article>

        <article className={`${styles.card} ${styles.cardLearn}`} ref={setAnchor('learn')}>
          <div className={styles.cardHead}>
            <img src={learnIcon} alt="" />
            <h3>{t('kitoApp.learn.title')}</h3>
          </div>
          <ol className={styles.cardList}>
            <li>
              <p className='bold'>{t('kitoApp.learn.item1Title')}</p>
              <p>{t('kitoApp.learn.item1Text')}</p>
            </li>
            <li>
              <p className='bold'>{t('kitoApp.learn.item2Title')}</p>
              <p>{t('kitoApp.learn.item2Text')}</p>
            </li>
            <li>
              <p className='bold'>{t('kitoApp.learn.item3Title')}</p>
              <p>{t('kitoApp.learn.item3Text')}</p>
            </li>
          </ol>
        </article>

        <article className={`${styles.card} ${styles.cardEmergency}`} ref={setAnchor('emergency')}>
          <div className={styles.cardHead}>
            <img src={emergencyIcon} alt="" />
            <h3>{t('kitoApp.emergency.title')}</h3>
          </div>
          <div className={styles.cardText}>
            <p className='bold'>{t('kitoApp.emergency.subtitle')}</p>
            <p>{t('kitoApp.emergency.text')}</p>
          </div>
        </article>

        {isDesktop && size.w > 0 && (
          <svg
            className={styles.arrowLayer}
            width={size.w}
            height={size.h}
            viewBox={`0 0 ${size.w} ${size.h}`}
          >
            {paths.map((p) => (
              <path key={p.key} d={p.d} className={styles.arrowPath} />
            ))}
          </svg>
        )}
      </div>

      <div className={styles.videoBlock}>
        <div className={styles.videoFrame}>
          <iframe
            src={isSwahili
              ? 'https://www.youtube.com/embed/0ZTy21cjBh8'
              : 'https://www.youtube.com/embed/hC24nmn8HKE'}
            title={t('kitoApp.h2')}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
}

export default KitoApp;
